import { Button, styled, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { useFetchData } from '../../hooks/useFetchData';
import { myFolder } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { StyledChatContainer } from '../../theme/CustomComponents';
import { Conversation } from '../../Types/conversation';
import { PaginationMode } from '../../Types/enums';
import { conversationsPathUrl } from '../../util/routesUrls';
import ChatCard from '../Chat/ChatCard';
import DebouncedSearchInput from '../DebouncedSearchInput';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

const SearchContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '16px',
  marginRight: '20px',
  paddingTop: '20px',
  '@media (max-width: 960px)': {
    width: '615px',
  },
});

const LoadMoreButton = styled(Button)({
  margin: '8px 16px',
  color: 'black',
  fontWeight: 'bold',
  width: '250px',
});

const PinnedChats = () => {
  const navigate = useClassicNavigate();
  const analytics = useAnalytics();
  const { selectedApp } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);

  const [searchQuery, setSearchQuery] = useState('');
  const [conversationSearchSelected, setConversationSearchSelected] = useState(false);

  const {
    data: conversations,
    pagination,
    nextPage,
    refetch: refetchConversations,
    resetPagination,
  } = useFetchData<Conversation>({
    urlPath: conversationsPathUrl(selectedApp, searchQuery, true, currentFolder?.id),
    paginated: true,
    paginationMode: PaginationMode.APPEND,
  });

  const { folderId } = useParams();

  const shouldShowLoadMoreButton = pagination?.hasNextPage ?? false;

  useEffect(() => {
    setSearchQuery('');
    setConversationSearchSelected(false);
    resetPagination();
  }, [folderId, resetPagination]);

  const loadMoreConversations = () => {
    analytics.event(AnalyticsEvent.WM_NIGEL_LOAD_MORE_CONVERSATIONS, undefined, {
      numConversationsOld: conversations?.length,
    });
    nextPage();
  };

  return (
    <div style={{ width: '100%' }}>
      <StyledChatContainer style={{ paddingRight: '0px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            paddingTop: '20px',
            gap: '20px',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5">Pinned Chats</Typography>
          </div>
          <div style={{ overflowY: 'hidden', height: 'calc(100vh - 100px)' }}>
            <SearchContainer>
              <DebouncedSearchInput
                value={searchQuery}
                valueUpdated={(value) => {
                  setSearchQuery(value);
                  resetPagination();
                }}
                onFocus={() => setConversationSearchSelected(true)}
                onBlur={() => setConversationSearchSelected(false)}
                shouldFocus={conversationSearchSelected}
              />
            </SearchContainer>
            {conversations?.length === 0 ? (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Typography variant="subtitle2">No conversations at the moment.</Typography>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  gap: '15px',
                  flexWrap: 'wrap',
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 250px)',
                }}
              >
                {conversations?.map((conversation) => (
                  <ChatCard
                    searchQuery={searchQuery}
                    key={conversation.id}
                    conversation={conversation}
                    IsSearchFocused={false}
                    refetchConversations={refetchConversations}
                    onConversationCardClick={() => {
                      navigate(
                        `/folder/${conversation.folderId ?? myFolder.id}/${conversation.id}`
                      );
                    }}
                  />
                ))}
                {shouldShowLoadMoreButton && (
                  <LoadMoreButton onClick={loadMoreConversations}>Load More +</LoadMoreButton>
                )}
              </div>
            )}
          </div>
        </div>
      </StyledChatContainer>
    </div>
  );
};

export default PinnedChats;
